import { asPlaybookId } from "@repo/app-types/entities/playbook";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import type { FC } from "react";
import { useMatch, useNavigate, useParams } from "react-router";
import { appPaths } from "../../app/appPaths.js";
import {
  PageBody,
  PageLayout,
  TableCard,
} from "../../components/layout/pageLayout.js";
import { PerformancePlaybookSwitcher } from "../../components/performancePlaybookSwitcher.js";
import { TeamDisplayToggle } from "./teamDisplayToggle.js";
import { useGradingsByGroup } from "./teamPerformanceByGroup.js";
import { useGradingsByTeammate } from "./teamPerformanceByTeammate.js";

type TeamDisplayMode = "teammates" | "groups";

// Each display mode is its own sibling route rendering this page; the mode is
// simply which of the two matched, and switching navigates between them.
const displayModePaths = {
  teammates: appPaths.playbookTeammates,
  groups: appPaths.playbookGroups,
} satisfies Record<
  TeamDisplayMode,
  { build(params: { playbookId: string }): string }
>;

export const TrainingTeamPageByTeam: FC = () => {
  const { playbookId: playbookIdParam } = useParams();
  if (!playbookIdParam) {
    throw new Error("Missing playbookId param.");
  }
  const playbookId = asPlaybookId(playbookIdParam);

  const isGroupsRoute = useMatch(displayModePaths.groups.pattern) !== null;
  const displayMode: TeamDisplayMode = isGroupsRoute ? "groups" : "teammates";

  const navigate = useNavigate();
  const onDisplayModeChange = (mode: TeamDisplayMode) => {
    navigate(displayModePaths[mode].build({ playbookId }));
  };

  const byTeammateContent = useGradingsByTeammate(playbookId);
  const byGroupContent = useGradingsByGroup(playbookId);

  const currentContent =
    displayMode === "groups" ? byGroupContent : byTeammateContent;

  return (
    <PageLayout className="h-full">
      <PageHeader
        title="Team"
        filterBar={currentContent.renderFilters({
          leadingItems: <PerformancePlaybookSwitcher />,
          rightActions: (
            <>
              <TeamDisplayToggle
                value={displayMode}
                onChange={onDisplayModeChange}
              />
              {currentContent.displayColumnsSelector}
            </>
          ),
        })}
      />

      <PageBody fills className="overflow-hidden">
        <TableCard className="flex-1">{currentContent.table}</TableCard>
      </PageBody>
    </PageLayout>
  );
};
